import { matchRoutes, RouteObject } from 'react-router-dom';
import { ENV } from 'types/env';
import { currentEnv, isDevOrLocalEnv } from 'utils/env';
import FallbackRoute from './FallbackRoute';
import LoginRoutes from './LoginRoutes';
import MainRoutes from './MainRoutes';
import OAuthConsentRoutes from './OAuthConsentRoutes';

// The MCP consent flow this route serves is wired up on local dev, DEV and DEMO; it is absent from
// every other deployed environment until MCP itself ships there.
export const appRoutes = [
    FallbackRoute,
    LoginRoutes,
    ...(isDevOrLocalEnv || currentEnv === ENV.DEMO ? [OAuthConsentRoutes] : []),
    MainRoutes,
] as RouteObject[];

// A route whose last segment is a tab param (`:tab`, `:invoiceTab`) shows a tab of the page the
// rest of the path names. An optional literal segment may sit between the page and the tab
// param (`/matter/:id/invoices/:invoiceTab`); it belongs to the tab when the page part still
// carries a param of its own, and to the page when it does not (`/invoices/:invoiceTab` is a
// tab of `/invoices`).
const TAB_ROUTE = /^(?<page>.*?)(?<literal>\/[^/:]+)?\/:[a-zA-Z]*[tT]ab$/;

/**
 * Identity of the page a URL shows, for deciding whether two history entries are "the same page"
 * to the back button.
 *
 * Every tab of a read view is the same page: switching tabs PUSHes a new history entry, so
 * `/matter/5` → `/matter/5/timecards` → `/matter/5/expenses` all resolve to `/matter/5`. Which
 * URLs are tabs comes from the route table — only a route ending in a tab param collapses — so a
 * nested read page such as a hypothetical `/matter/:id/documents/:documentId` keeps its own
 * identity. The query string never counts.
 */
export function pageKeyForRoutes(routes: RouteObject[], url: string): string {
    const pathname = url.split('?')[0].replace(/\/+$/, '') || '/';
    const matches = matchRoutes(routes, pathname);
    const routePath = matches?.at(-1)?.route.path;
    const tab = routePath ? TAB_ROUTE.exec(routePath) : null;
    if (!tab?.groups) {
        return pathname;
    }
    const literalBelongsToTab = tab.groups.literal !== undefined && tab.groups.page.includes(':');
    const segmentsToDrop = literalBelongsToTab ? 2 : 1;
    const segments = pathname.split('/').filter(Boolean);
    return `/${segments.slice(0, segments.length - segmentsToDrop).join('/')}`;
}

export function pageKey(url: string): string {
    return pageKeyForRoutes(appRoutes, url);
}
